import { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag, Star,
  Mail, Inbox, Settings as SettingsIcon, Menu, Send, Download, Building2, CreditCard,
  RefreshCw, Undo2, AlertTriangle, Target, ShieldAlert,
} from "lucide-react";
import { StripeLiveModeBanner } from "@/components/admin/StripeLiveModeBanner";
import { AdminNotificationsBell } from "@/components/admin/AdminNotificationsBell";
import { WebhookHealthBanner } from "@/components/admin/WebhookHealthBanner";

const nav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Health", to: "/admin/health", icon: AlertTriangle },
  { label: "Notifications", to: "/admin/notifications", icon: Mail },
  { label: "Leads (CRM)", to: "/admin/leads", icon: Target },
  { label: "Promo Codes", to: "/admin/promos", icon: Star },
  { label: "Landing Pages", to: "/admin/landing-pages", icon: FileText },
  { label: "Email Campaigns", to: "/admin/campaigns", icon: Send },
  { label: "Promo Analytics", to: "/admin/promos/analytics", icon: Target },
  { label: "UTM Builder", to: "/admin/utm-builder", icon: FileText },
  { label: "Attribution Test", to: "/admin/attribution-test", icon: AlertTriangle },
  { label: "Availability", to: "/admin/availability", icon: Calendar },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Orders", to: "/admin/orders", icon: ShoppingBag },
  { label: "Customers", to: "/admin/customers", icon: Users },
  { label: "Studios", to: "/admin/studios", icon: Building2 },
  { label: "Add-ons", to: "/admin/addons", icon: ShoppingBag },
  { label: "Stock Audit", to: "/admin/addons/audit", icon: AlertTriangle },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Stripe Sync", to: "/admin/stripe/sync", icon: RefreshCw },
  { label: "Stripe Events", to: "/admin/stripe/events", icon: CreditCard },
  { label: "Stripe Webhook Status", to: "/admin/stripe/webhook-status", icon: CreditCard },
  { label: "Stripe Webhook Checklist", to: "/admin/stripe/webhook-checklist", icon: CreditCard },
  { label: "Webhook Timeline", to: "/admin/stripe/webhook-timeline", icon: CreditCard },
  { label: "Stripe Key Swap", to: "/admin/stripe/key-swap", icon: CreditCard },
  { label: "Slot Attempts", to: "/admin/availability/attempts", icon: AlertTriangle },
  { label: "Slot Timeline", to: "/admin/availability/timeline", icon: Calendar },
  { label: "Email Templates", to: "/admin/emails/templates", icon: Mail },
  { label: "Prod Smoke Test", to: "/admin/prod-smoke-test", icon: AlertTriangle },
  { label: "Run Booking Test", to: "/admin/run-booking-test", icon: AlertTriangle },
  { label: "Maternity Funnel", to: "/admin/funnels/maternity", icon: Target },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Spam Review", to: "/admin/spam", icon: ShieldAlert },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
  { label: "Refund Policies", to: "/admin/refund-policies", icon: Undo2 },
];

export function AdminShell({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <div className="min-h-screen bg-background">
      <button onClick={() => setOpen(o => !o)} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform overflow-y-auto ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {nav.map((item) => (
            <Link key={item.to} to={item.to}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                loc.pathname === item.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"
              }`}>
              <item.icon className="w-4 h-4" /> {item.label}
            </Link>
          ))}
        </nav>
      </aside>
      <main className="lg:ml-64 p-6 pt-24">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <h1 className="text-2xl font-display tracking-tight">{title}</h1>
          <div className="flex items-center gap-2">
            {actions}
            <AdminNotificationsBell />
          </div>
        </div>
        <StripeLiveModeBanner />
        <WebhookHealthBanner />
        {children}
      </main>
    </div>
  );
}